import React, { useContext, useEffect, useState } from "react";
import { FiCamera, FiUpload } from "react-icons/fi";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { ProfileContext } from "../contexts/ProfileContext";
import { db, storage } from "../js/firebase";
import { showErrorMessage, showSuccessMessage } from "../helpers/useToast";
//import { OrganizationDetailsModal } from "./organization-details-modal";

export default function AccountProfile() {
    const { worker, organization: currentOrganization, organizationFirebaseId } = useContext(ProfileContext);
    const [activeTab, setActiveTab] = useState("profile");
    const [profileImage, setProfileImage] = useState("/profile-image.jpg");
    // eslint-disable-next-line
    const [profileFile, setProfileFile] = useState(null);
    const [isUploadingProfile, setIsUploadingProfile] = useState(false);
    const [originalEmail, setOriginalEmail] = useState("");
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [isSavingOrganization, setIsSavingOrganization] = useState(false);
    const [isUploadingOrganizationLogo, setIsUploadingOrganizationLogo] = useState(false);
    const [profileErrors, setProfileErrors] = useState({});
    const [organizationErrors, setOrganizationErrors] = useState({});

    const [profile, setProfile] = useState({
        fullName: "",
        displayName: "",
        email: "",
        password: "",
        organizationName: ""
    });

    const [organization, setOrganization] = useState({
        name: "Crossroads Christian Communications Inc.",
        website: "https://crossroads.ca",
        phone: "1-905-335-7100",
        address: "1295 North Service Rd, Burlington, ON L7R 4X5",
        serviceStartTimes: "",
        churchPrograms: "",
        prayerlineUrl: "",
        description: "",
    });
    const [savedOrganization, setSavedOrganization] = useState(organization);

    const canEditOrganization = worker?.role === "subscriber";
    const currentOrganizationId = currentOrganization?.id || organizationFirebaseId;

    const handleProfileChange = (e) => {
        setProfile({
            ...profile,
            [e.target.name]: e.target.value,
        });

        setProfileErrors((prev) => ({
            ...prev,
            [e.target.name]: "",
        }));
    };
    // Resize image file if it exceeds max dimensions or size. Downscales only; does not upscale.
    const resizeImageFileIfNeeded = (file, maxWidth, maxHeight, maxBytes) => {
        return new Promise((resolve, reject) => {
            try {
                if (!file || !file.type || file.type === 'image/svg+xml') return resolve(file);

                const url = URL.createObjectURL(file);
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.onload = async () => {
                    try {
                        let { width: w, height: h } = img;
                        // compute scale to fit within max dims (downscale only)
                        const scale = Math.min(1, maxWidth / w, maxHeight / h);
                        const newW = Math.round(w * scale);
                        const newH = Math.round(h * scale);

                        // If no downscale needed and size under limit, return original
                        if (scale === 1 && (!maxBytes || file.size <= maxBytes)) {
                            URL.revokeObjectURL(url);
                            return resolve(file);
                        }

                        const canvas = document.createElement('canvas');
                        canvas.width = newW;
                        canvas.height = newH;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, newW, newH);

                        // Choose output type: preserve PNG/WebP if possible, otherwise JPEG
                        let outputType = file.type && (file.type === 'image/png' || file.type === 'image/webp') ? file.type : 'image/jpeg';

                        // Try to get a blob and reduce quality until under maxBytes
                        let quality = 0.92;
                        const toBlob = (type, q) => new Promise((res) => canvas.toBlob(res, type, q));
                        let blob = await toBlob(outputType, quality);

                        if (maxBytes) {
                            // If PNG and too large, try converting to JPEG to reduce size
                            if (blob && blob.size > maxBytes && outputType === 'image/png') {
                                outputType = 'image/jpeg';
                                quality = 0.92;
                                blob = await toBlob(outputType, quality);
                            }

                            while (blob && blob.size > maxBytes && quality > 0.5) {
                                quality -= 0.07;
                                blob = await toBlob(outputType, quality);
                            }
                        }

                        if (!blob) {
                            URL.revokeObjectURL(url);
                            return resolve(file);
                        }

                        const newFile = new File([blob], file.name.replace(/\.[^.]+$/, '') + (outputType === 'image/jpeg' ? '.jpg' : (outputType === 'image/png' ? '.png' : '.webp')), { type: blob.type });
                        URL.revokeObjectURL(url);
                        return resolve(newFile);
                    } catch (err) {
                        try { URL.revokeObjectURL(url); } catch (e) {}
                        return reject(err);
                    }
                };
                img.onerror = () => {
                    try { URL.revokeObjectURL(url); } catch (e) {}
                    return resolve(file);
                };
                img.src = url;
            } catch (err) {
                return resolve(file);
            }
        });
    };

    const handleOrganizationChange = (e) => {
        setOrganization({
            ...organization,
            [e.target.name]: e.target.value,
        });
        setOrganizationErrors((prev) => ({ ...prev, [e.target.name]: "" }));
    };

    const handleProfileImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Basic client-side validation: ensure it's an image
        if (!file.type.startsWith("image/")) {
            showErrorMessage("Please select a valid image file.");
            e.target.value = "";
            return;
        }

        // Automatic client-side resize (downscale only) to improve upload reliability
        let uploadFile = file;
        try {
            uploadFile = await resizeImageFileIfNeeded(file, 400, 400, 3 * 1024 * 1024);
        } catch (resizeErr) {
            console.warn('Image resizing failed, will try to upload original file', resizeErr);
            uploadFile = file;
        }

        const previousProfileImage = profileImage;
        let objectUrl;
        try {
            objectUrl = URL.createObjectURL(uploadFile);
            setProfileImage(objectUrl);
            setProfileFile(uploadFile);
        } catch (err) {
            console.warn("Could not create preview for selected image", err);
            setProfileFile(file);
        }

        // Upload immediately with email
        try {
            setIsUploadingProfile(true);
            const currentEmail = originalEmail || profile.email || "";
            if (!currentEmail) {
                showErrorMessage("Unable to determine user email for upload");
                setProfileImage(previousProfileImage);
                setProfileFile(null);
                e.target.value = "";
                return;
            }
            const formData = new FormData();
            formData.append("_method", "PUT");
            formData.append("email", currentEmail);
            formData.append("profile", file);

            const resp = await fetch(`${process.env.REACT_APP_API_URL}/auth0/user`, {
                method: "POST",
                headers: {
                    Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                    Accept: "application/json",
                },
                body: formData,
            });

            if (!resp.ok) {
                const errorData = await resp.json().catch(() => ({}));
                showErrorMessage(errorData.error || "Failed to upload profile image");
                setProfileImage(previousProfileImage);
                return;
            }

            const result = await resp.json().catch(() => ({}));
            const updatedUser = result?.user;
            if (updatedUser?.profile) {
                setProfileImage(updatedUser.profile);
            }
            setProfileFile(null);
            showSuccessMessage("Profile image uploaded successfully");
        } catch (err) {
            console.error("Failed to upload profile image:", err);
            showErrorMessage("Failed to upload profile image");
            setProfileImage(previousProfileImage);
        } finally {
            setIsUploadingProfile(false);
            try {
                if (objectUrl) URL.revokeObjectURL(objectUrl);
            } catch (e) {
                // ignore
            }
            e.target.value = "";
        }
    };

    useEffect(() => {
        if (!currentOrganization) return;

        const loadOrganizationLogo = async (organizationDetails) => {
            const extensions = ["png", "jpg", "jpeg", "webp", "svg"];
            const candidates = [];
            const addCandidate = (path) => {
                if (path && !candidates.includes(path)) candidates.push(path);
            };

            addCandidate(organizationDetails.logoUrl);
            if (currentOrganizationId) {
                addCandidate(`logos/${currentOrganizationId}`);
                extensions.forEach((extension) => addCandidate(`logos/${currentOrganizationId}.${extension}`));
            }
            if (organizationDetails.name) {
                extensions.forEach((extension) => addCandidate(`logos/${organizationDetails.name}.${extension}`));
            }

            for (const candidate of candidates) {
                try {
                    const url = candidate.startsWith("http")
                        ? candidate
                        : await getDownloadURL(ref(storage, candidate));

                    await new Promise((resolve, reject) => {
                        const image = new Image();
                        image.onload = resolve;
                        image.onerror = reject;
                        image.src = url;
                    });
                    setOrganization((prev) => ({ ...prev, logoUrl: url }));
                    setSavedOrganization((prev) => ({ ...prev, logoUrl: url }));
                    return;
                } catch (error) {
                    // Try the next legacy or document-ID path.
                }
            }
        };

        const organizationDetails = {
            name: currentOrganization.name || "",
            website: currentOrganization.website || "",
            phone: currentOrganization.phone || currentOrganization.organizationPhoneNumber || "",
            address: currentOrganization.address || "",
            logoUrl: "",
            serviceStartTimes: currentOrganization.serviceStartTimes || "",
            prayerlineUrl: currentOrganization.prayerlineUrl || "",
            description: currentOrganization.description || "",
            churchPrograms: currentOrganization.churchPrograms || "",
        };
        setOrganization(organizationDetails);
        setSavedOrganization(organizationDetails);
        loadOrganizationLogo(organizationDetails);
    }, [currentOrganization, currentOrganizationId]);

    const loadUserProfile = async () => {
        try {
            const email = worker?.email ;
            const url = `${process.env.REACT_APP_API_URL}/auth0/user?email=${encodeURIComponent(email)}`;
            const options = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                    Accept: "application/json",
                },
            };

            const response = await fetch(url, options);
            if (!response.ok) {
                console.error("Failed to fetch user profile", response.status);
                return;
            }

            const data = await response.json();
            const userData = data?.user;

            if (!userData) return;

            setProfile((prev) => ({
                ...prev,
                fullName: userData.full_name || "",
                displayName: userData.display_name || "",
                email: userData.email || "",
                organizationName: userData.organization_name || "",
            }));
           
            setOriginalEmail(userData.email || "");

            if (userData.profile) {
                setProfileImage(userData.profile);
            }
        } catch (error) {
            console.error("Failed to load account profile:", error);
        }
    };

    useEffect(() => {
        if (worker) {
            loadUserProfile();
        }
        // eslint-disable-next-line
    }, [worker]);

    const handleSaveProfile = async () => {
        const errors = {};

        if (!profile.fullName?.trim()) {
            errors.fullName = "Full Name is required.";
        }

        if (!profile.displayName?.trim()) {
            errors.displayName = "Display Name is required.";
        }

        if (!profile.email?.trim()) {
            errors.email = "Email Address is required.";
        } else if (!/^\S+@\S+\.\S+$/.test(profile.email.trim())) {
            errors.email = "Enter a valid email address.";
        }

        if (profile.password?.trim() && profile.password.trim().length < 8) {
            errors.password = "Password must be at least 8 characters.";
        }

        if (Object.keys(errors).length > 0) {
            setProfileErrors(errors);
            return;
        }

        try {
            setIsSavingProfile(true);
            setProfileErrors({});
            const currentEmail = originalEmail;

            const payload = new FormData();
            payload.append("_method", "PUT");
            payload.append("email", currentEmail);

            if (profile.fullName?.trim()) {
                payload.append("full_name", profile.fullName.trim());
            }

            if (profile.displayName?.trim()) {
                payload.append("display_name", profile.displayName.trim());
            }

            if (currentEmail !== profile.email) {
                payload.append("new_email", profile.email.trim());
            }

            if (profile.password?.trim()) {
                payload.append("password", profile.password);
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth0/user`, {
                method: "POST",
                headers: {
                    Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                    Accept: "application/json",
                },
                body: payload,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                showErrorMessage(errorData.error || "Failed to update user profile");
                return;
            }

            const updated = await response.json();
            const updatedUser = updated?.user;

            setProfile((prev) => ({
                ...prev,
                fullName: updatedUser?.full_name || prev.fullName,
                displayName: updatedUser?.display_name || prev.displayName,
                email: updatedUser?.email || prev.email,
                password: "",
            }));
            setOriginalEmail(updatedUser?.email || profile.email);
            if (updatedUser?.profile) {
                setProfileImage(updatedUser.profile);
            }
            // Clear selected file after successful upload
            setProfileFile(null);
            showSuccessMessage("Profile updated successfully");

        } catch (error) {
            console.error("Failed to save account profile:", error);
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleSaveOrganization = async () => {
        if (!currentOrganizationId) {
            showErrorMessage("Unable to find your organization");
            return;
        }
        // Validate website and phone formats
        const errors = {};

        const website = (organization.website || "").trim();
        if (website) {
            try {
                const parsed = new URL(website);
                if (!/^https?:$/.test(parsed.protocol)) {
                    errors.website = "Website must start with http:// or https://";
                }
            } catch (e) {
                errors.website = "Enter a valid website URL (include http:// or https://)";
            }
        }

        const phone = (organization.phone || "").trim();
        if (phone) {
            // allow digits, spaces, parentheses, plus, dashes and dots
            const allowed = /^[0-9()+.\s-]+$/;
            if (!allowed.test(phone)) {
                errors.phone = "Phone number contains invalid characters";
            } else {
                const digits = phone.replace(/\D/g, "");
                if (digits.length < 7 || digits.length > 15) {
                    errors.phone = "Enter a valid phone number (7-15 digits)";
                }
            }
        }

        if (Object.keys(errors).length > 0) {
            setOrganizationErrors(errors);
            if (errors.website) showErrorMessage(errors.website);
            if (errors.phone) showErrorMessage(errors.phone);
            return;
        }

        // If website provided, attempt to ping/check it's live
        const websiteValue = (organization.website || "").trim();
        if (websiteValue) {
            const isWebsiteLive = async (url) => {
                try {
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 5000);

                    // Try HEAD first
                    let resp;
                    try {
                        resp = await fetch(url, { method: 'HEAD', signal: controller.signal });
                    } catch (headErr) {
                        // Some servers disallow HEAD; try GET
                        try {
                            resp = await fetch(url, { method: 'GET', signal: controller.signal });
                        } catch (getErr) {
                            // as a last resort, try with no-cors (may return opaque response)
                            try {
                                resp = await fetch(url, { method: 'GET', mode: 'no-cors', signal: controller.signal });
                            } catch (nocorsErr) {
                                resp = null;
                            }
                        }
                    }

                    clearTimeout(timeoutId);

                    if (!resp) return false;
                    // If opaque response (no-cors) we can't read status; treat as live
                    if (resp.type === 'opaque') return true;
                    return resp.ok;
                } catch (err) {
                    return false;
                }
            };

            const live = await isWebsiteLive(websiteValue).catch(() => false);
            if (!live) {
                const msg = "Website appears unreachable. Please verify the URL or try again.";
                setOrganizationErrors((prev) => ({ ...prev, website: msg }));
                showErrorMessage(msg);
                return;
            }
        }

        try {
            setIsSavingOrganization(true);
            setOrganizationErrors({});
            const organizationRef = doc(db, "organizations", currentOrganizationId);
            const changes = {
                website: organization.website,
                phone: organization.phone,
                organizationPhoneNumber: organization.phone,
                address: organization.address,
                serviceStartTimes: organization.serviceStartTimes,
                churchPrograms: organization.churchPrograms,
                prayerlineUrl: organization.prayerlineUrl,
                description: organization.description,
            };

            await updateDoc(organizationRef, changes);
            const updatedOrganization = { ...organization, ...changes };
            setOrganization(updatedOrganization);
            setSavedOrganization(updatedOrganization);
            showSuccessMessage("Organization details saved successfully");
        } catch (error) {
            console.error("Failed to save organization details:", error);
            showErrorMessage("Failed to save organization details");
        } finally {
            setIsSavingOrganization(false);
        }
    };

    const handleOrganizationLogoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !currentOrganizationId) {
            if (!currentOrganizationId) {
                showErrorMessage("Unable to find your organization");
            }
            return;
        }
        // Attempt client-side downscale to improve upload success
        let uploadFile = file;
        try {
            uploadFile = await resizeImageFileIfNeeded(file, 512, 512, 2 * 1024 * 1024);
        } catch (resizeErr) {
            console.warn('Organization logo resizing failed, will upload original file', resizeErr);
            uploadFile = file;
        }

        try {
            console.log('Organization logo upload started', { fileName: uploadFile.name, fileType: uploadFile.type, fileSize: uploadFile.size, currentOrganizationId });
            setIsUploadingOrganizationLogo(true);
            const previousLogoUrl = organization.logoUrl || currentOrganization?.logoUrl || "";
            // Use a stable storage path and upload with metadata
            const extension = (uploadFile.name.split(".").pop() || "png").toLowerCase();
            const path = `logos/${currentOrganizationId}.${extension}`;
            const storageReference = ref(storage, path);

            // Use resumable upload for better reliability and set content type
            const metadata = { contentType: file.type || `image/${extension}` };
            // uploadBytesResumable returns an uploadTask
            const uploadTask = uploadBytesResumable(storageReference, uploadFile, metadata);

            // Log progress and await completion
            await new Promise((resolve, reject) => {
                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        console.log(`Upload is ${progress}% done`, { bytesTransferred: snapshot.bytesTransferred, totalBytes: snapshot.totalBytes, state: snapshot.state });
                    },
                    (err) => {
                        console.error('Upload failed with error', err);
                        reject(err);
                    },
                    () => {
                        console.log('Upload completed');
                        resolve();
                    }
                );
            });

            const logoUrl = await getDownloadURL(storageReference);
            console.log('Got download URL for logo:', logoUrl);
            await updateDoc(doc(db, "organizations", currentOrganizationId), { logoUrl });
            console.log('Firestore organization document updated with new logoUrl');

            // Attempt to delete previous logo if it points to storage
            if (previousLogoUrl && previousLogoUrl !== logoUrl) {
                try {
                    if (previousLogoUrl.startsWith('http')) {
                        // Try to extract storage path from a Firebase download URL: look for '/o/<encodedPath>'
                        const m = previousLogoUrl.match(/\/o\/(.*?)\?/);
                        if (m && m[1]) {
                            const decoded = decodeURIComponent(m[1]);
                            await deleteObject(ref(storage, decoded));
                        } else {
                            // If we can't parse, skip deletion to avoid errors
                            console.warn('Previous logo is a remote URL and could not be parsed for deletion');
                        }
                    } else {
                        // previousLogoUrl is likely a storage path
                        await deleteObject(ref(storage, previousLogoUrl));
                    }
                } catch (deleteError) {
                    console.warn("Organization logo updated, but old logo could not be deleted:", deleteError);
                }
            }

            setOrganization((prev) => ({ ...prev, logoUrl }));
            setSavedOrganization((prev) => ({ ...prev, logoUrl }));
            showSuccessMessage("Organization logo uploaded successfully");
        } catch (error) {
            console.error("Failed to upload organization logo:", error);
            showErrorMessage("Failed to upload organization logo");
        } finally {
            setIsUploadingOrganizationLogo(false);
            e.target.value = "";
        }
    };

    const handleCancelOrganizationChanges = () => {
        setOrganization(savedOrganization);
    };

    return (
        <div className="min-h-screen bg-[#EEF4F5] p-6">
            {/* Header */}
            <div className="mb-4">
                <h1 className="text-3xl text-[#0B3D44]">
                    Account & Profile
                </h1>
                <p className="text-[#2CB3C5] mt-2 tracking-wide">
                    Update your personal info and organization settings
                </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-3 mb-6">
                <button
                    onClick={() => setActiveTab("profile")}
                    className={`w-[250px] h-12 rounded-xl border
          ${activeTab === "profile"
                            ? "bg-[#7DB8BE] text-white"
                            : "bg-white text-[#2CB3C5] border-[#2CB3C5]"
                        }`}
                >
                    MY PROFILE
                </button>
                
                <button
                    onClick={() => setActiveTab("organization")}
                    className={`w-[250px] h-12 rounded-xl border
          ${activeTab === "organization"
                            ? "bg-[#7DB8BE] text-white"
                            : "bg-white text-[#2CB3C5] border-[#2CB3C5]"
                        }`}
                >
                    ORGANIZATION
                </button>
            </div>

            {/* Content */}
            <div className="bg-white rounded-2xl border border-[#CBE3E7] p-4">

            {/* MY PROFILE */}
            {activeTab === "profile" && (
                    <>
                    <div className="flex items-center justify-center">
                    <div className="flex items-center gap-10  p-1 rounded-lg w-full max-w-3xl">
                            {/* Profile Image */}
                      
                            <div className="relative">
                                <div className="w-28 h-28 rounded-full overflow-hidden bg-gray-200">
                                    <img
                                        src={profileImage}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                    <p className="text-[#2CB3C5] text-xs mt-1 text-center">Profile Picture (Circle UI) — Recommended: 200 × 200 px to 400 × 400 px. Max size: 3 MB. PNG, JPG, WEBP accepted.</p>
                                    <input id="profileUpload" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleProfileImageChange} disabled={isUploadingProfile} />
                                    <label htmlFor="profileUpload" title={isUploadingProfile ? "Uploading..." : undefined} className={`absolute bottom-0 right-0 bg-white rounded-full p-1 shadow ${isUploadingProfile ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <FiCamera size={18} />
                                    </label>
                                </div>      
                                 
                             </div>
                                
                            {/* Content */}
                            <div className="flex flex-col">
                                <h2 className="text-[18px] font-semibold text-[#083C4D] leading-tight">
                                    {profile.fullName || "-"}
                                </h2>

                                <p className="mt-1 text-[#36B6C6] text-[14px] tracking-[3px] leading-relaxed">
                                    {profile.organizationName || "-"}
                                </p>

                            </div>
                        </div>
                        </div>
                <div className="grid grid-cols-2 gap-4">
                    <InputField
                        label="Full Name"
                        name="fullName"
                        value={profile.fullName}
                        onChange={handleProfileChange}
                        error={profileErrors.fullName}
                    />

                    <InputField
                        label="Display Name"
                        name="displayName"
                        value={profile.displayName}
                        onChange={handleProfileChange}
                        error={profileErrors.displayName}
                    />

                    <InputField
                        label="Email Address"
                        name="email"
                        value={profile.email}
                        onChange={handleProfileChange}
                                error={profileErrors.email}
                                disabled={true}
                                disabledHint="Email address can only be changed by an administrator."
                            />



                    <InputField
                        label="New Password"
                        name="password"
                        value={profile.password}
                        onChange={handleProfileChange}
                        type="password"
                        error={profileErrors.password}
                    />
                        </div>
                        {/* Footer */}
                        <div className="flex justify-end gap-4 border-t mt-12 pt-8">
                            <button className="bg-[#FF7433] text-white px-6 py-2 rounded-xl" onClick={loadUserProfile}>                            
                                CANCEL
                            </button>

                            <button
                                onClick={handleSaveProfile}
                                disabled={isSavingProfile}
                                className="bg-[#36B6C6] text-white px-6 py-2 rounded-xl disabled:opacity-60"
                            >
                                {isSavingProfile ? "SAVING..." : "SAVE CHANGES"}
                            </button>
                        </div>
            </>
        )}

            {/* ORGANIZATION */}
            {activeTab === "organization" && (
                <>
                    <div className="border border-dashed border-[#B5DDE2] rounded-3xl p-2 mb-10">
                        <div className="flex gap-4 items-center">
                            <div className="w-28 h-28 rounded-xl bg-[#EEF4F5] flex items-center justify-center overflow-hidden">
                                {organization.logoUrl ? (
                                    <img src={organization.logoUrl} alt="Organization logo" className="w-full h-full object-contain" />
                                ) : (
                                    <span className="text-sm text-[#2CB3C5]">LOGO</span>
                                )}
                            </div>

                            <div>
                                <h3 className="font-semibold text-xl">
                                    Organization Logo
                                </h3>

                                <p className="text-[#2CB3C5] text-sm">
                                        PNG, JPG, SVG - Recommended 400x400
                                </p>

                                    <input id="organizationLogoUpload" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleOrganizationLogoChange} disabled={!canEditOrganization || !currentOrganizationId || isUploadingOrganizationLogo} />
                                    <label htmlFor="organizationLogoUpload" className={`mt-1 w-fit px-3 py-2 border border-[#B7D7DB] rounded-lg bg-[#DCEBEC] text-[#083C4D] text-sm font-semibold tracking-[2px] flex items-center gap-2 hover:bg-[#d2e3e5] transition ${canEditOrganization && currentOrganizationId ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                                        {isUploadingOrganizationLogo ? "Uploading..." : "Upload"}
                                        <FiUpload size={18} />
                                    </label>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <InputField
                            label="Organization Name (Read-only)"
                            name="name"
                            value={organization.name}
                            onChange={handleOrganizationChange}
                            disabled
                            disabledHint="Organization name can only be changed by an administrator."
                        />

                        <InputField
                            label="Website"
                            name="website"
                            value={organization.website}
                            onChange={handleOrganizationChange}
                            disabled={!canEditOrganization}
                            error={organizationErrors.website}
                        />

                        <InputField
                            label="Phone"
                            name="phone"
                            value={organization.phone}
                            onChange={handleOrganizationChange}
                            disabled={!canEditOrganization}
                            error={organizationErrors.phone}
                        />

                        <InputField
                            label="Address"
                            name="address"
                            value={organization.address}
                            onChange={handleOrganizationChange}
                            disabled={!canEditOrganization}
                            />
                            <InputField
                                label="Sunday Service Start Times"
                                name="serviceStartTimes"
                                value={organization.serviceStartTimes}
                                onChange={handleOrganizationChange}
                                disabled={!canEditOrganization}
                                error={organizationErrors.serviceStartTimes}
                                maxLength={300}
                            />

                            <InputField
                                label="Church Programs"
                                name="churchPrograms"
                                value={organization.churchPrograms}
                                onChange={handleOrganizationChange}
                                disabled={!canEditOrganization}
                                error={organizationErrors.churchPrograms}
                                maxLength={300}
                            />
                            <InputField
                                label="Prayer Line Landing Page"
                                name="prayerlineUrl"
                                value={organization.prayerlineUrl}
                                onChange={handleOrganizationChange}
                                disabled={!canEditOrganization}
                                error={organizationErrors.prayerlineUrl}
                                maxLength={200}
                            />

                            <InputField
                                label="Subscriber Description"
                                name="description"
                                value={organization.description}
                                onChange={handleOrganizationChange}
                                disabled={!canEditOrganization}
                                error={organizationErrors.description}
                                maxLength={300}
                            />
                        </div>
                        {/* Footer */}
                        <div className="flex justify-end gap-4 border-t mt-6 pt-3">
                            <button onClick={handleCancelOrganizationChanges} disabled={!canEditOrganization || isSavingOrganization} className="bg-[#FF7433] text-white px-6 py-2 rounded-xl disabled:opacity-60">
                                CANCEL
                            </button>

                            <button onClick={handleSaveOrganization} disabled={!canEditOrganization || isSavingOrganization} className="bg-[#36B6C6] text-white px-6 py-2 rounded-xl disabled:opacity-60">
                                {isSavingOrganization ? "SAVING..." : "SAVE CHANGES"}
                            </button>
                        </div>
                </>
            )}

           
        </div>
    </div >
  );
}

const InputField = ({
    label,
    name,
    value,
    onChange,
    type = "text",
    error,
    disabled = false,
    disabledHint,
}) => (
    <div>
        <label className="block mb-2 font-semibold text-[#0B3D44]">
            {label}
        </label>

        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            maxLength={name === "phone" ? 20 : name === "website" ? 60 : 300}
            disabled={disabled}
            aria-disabled={disabled}
            title={disabled ? disabledHint || `${label} is disabled` : undefined}
            className="
        w-full
        h-12
        rounded-xl
        border
        border-[#BCDDE0]
        px-4
        bg-[#F5FAFA]
        focus:outline-none
        focus:ring-2
        focus:ring-[#36B6C6]
                disabled:bg-[#D7E0E2]
                disabled:text-[#607277]
                disabled:border-[#A9B8BB]
                disabled:cursor-not-allowed
      "

        />
                {disabled && disabledHint && (
                        <p className="text-[#607277] text-xs mt-1">{disabledHint}</p>
                )}
        {error && <p className="text-[#FF7433] text-sm mt-1">{error}</p>}
    </div>
);

